/**
 * AdminReminders — Platform admin can:
 *   • Preview which shops are in each trigger window (15/7/3/0/-1/-3/-7 day offsets)
 *   • Click "Run Reminders Now" to dispatch (cron runs the same dispatcher at
 *     9 a.m. PKT daily; this is the manual override).
 *   • Browse the full reminder_log (audit trail).
 *   • Click each shop's wa.me link to send the WhatsApp message personally.
 */
import React, { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { Button } from "../../components/ui/button";
import {
  BellRing, MessageCircle, RefreshCcw, AlertTriangle, Clock, CalendarDays, Hourglass,
} from "lucide-react";
import { toast } from "sonner";

const OFFSET_META = {
  "15": { label: "15 days before expiry", tone: "bg-sky-500/15 text-sky-300 border-sky-500/30", icon: CalendarDays },
  "7":  { label: "7 days before expiry",  tone: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30", icon: Clock },
  "3":  { label: "3 days before expiry",  tone: "bg-amber-500/15 text-amber-300 border-amber-500/30", icon: Clock },
  "0":  { label: "Expiring today",        tone: "bg-orange-500/15 text-orange-300 border-orange-500/30", icon: BellRing },
  "-1": { label: "1 day overdue",         tone: "bg-red-500/15 text-red-300 border-red-500/30", icon: AlertTriangle },
  "-3": { label: "3 days overdue",        tone: "bg-red-500/20 text-red-300 border-red-500/40", icon: AlertTriangle },
  "-7": { label: "7 days overdue",        tone: "bg-red-500/30 text-red-200 border-red-500/50", icon: AlertTriangle },
};

const offsetOrder = ["15", "7", "3", "0", "-1", "-3", "-7"];

export default function AdminReminders() {
  const [preview, setPreview] = useState({ total: 0, by_offset: {} });
  const [log, setLog] = useState([]);
  const [busy, setBusy] = useState(false);
  const [lastRun, setLastRun] = useState(null);

  const load = async () => {
    const [p, l] = await Promise.all([
      api.get("/reminders/preview").then((r) => r.data),
      api.get("/reminders/log?limit=100").then((r) => r.data),
    ]);
    setPreview(p);
    setLog(l);
  };

  useEffect(() => { load().catch(() => {}); }, []);

  const runNow = async () => {
    if (busy) return;
    if (!window.confirm("Send reminders to all shops currently in the 15/7/3/0/-1/-3/-7 day windows? Already-sent shops are skipped automatically.")) return;
    setBusy(true);
    try {
      const { data } = await api.post("/reminders/run");
      setLastRun(data);
      toast.success(`Dispatched: ${data.sent} sent · ${data.skipped} skipped · ${data.errors} errors`);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-reminders-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="font-display text-3xl font-bold text-white flex items-center gap-2">
            <BellRing className="w-7 h-7 text-amber-400" /> Renewal Reminders
          </h1>
          <p className="text-slate-400 mt-1">
            Daily 9:00 PKT auto-dispatcher delivers in-app + WhatsApp reminders to shops whose subscription is 15 / 7 / 3 / 0 days from expiry, or 1 / 3 / 7 days overdue.
          </p>
        </div>
        <div className="flex gap-2">
          <Button onClick={load} variant="ghost" className="text-slate-300" data-testid="reminders-reload">
            <RefreshCcw className="w-4 h-4 mr-1" /> Refresh
          </Button>
          <Button onClick={runNow} disabled={busy} className="bg-amber-500 hover:bg-amber-600 text-slate-900 font-semibold" data-testid="reminders-run-now">
            <BellRing className="w-4 h-4 mr-1" /> {busy ? "Running…" : "Run Reminders Now"}
          </Button>
        </div>
      </div>

      {lastRun && (
        <div className="rounded-md bg-emerald-500/10 border border-emerald-500/30 px-4 py-3 text-emerald-200 text-sm" data-testid="reminders-last-run">
          Last manual run: <strong>{lastRun.sent}</strong> sent, <strong>{lastRun.skipped}</strong> skipped, <strong>{lastRun.errors}</strong> errors.
        </div>
      )}

      {/* Preview windows */}
      <div>
        <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-2">
          In Trigger Window — Total {preview.total}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
          {offsetOrder.map((o) => {
            const meta = OFFSET_META[o] || { label: o, tone: "bg-slate-700 text-slate-300", icon: Clock };
            const shops = preview.by_offset?.[o] || [];
            const Icon = meta.icon;
            return (
              <div key={o} className="bg-[#151A23] border border-slate-800 rounded-md p-4" data-testid={`preview-window-${o}`}>
                <div className={`inline-flex items-center gap-1 rounded-full ${meta.tone} border px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider mb-3`}>
                  <Icon className="w-3 h-3" />{meta.label}
                </div>
                <div className="font-display text-3xl font-bold text-white tabular">{shops.length}</div>
                <div className="text-xs text-slate-500 mt-1">shops</div>
                {shops.length > 0 && (
                  <ul className="mt-3 space-y-1 max-h-32 overflow-y-auto text-xs text-slate-300 pr-1">
                    {shops.slice(0, 8).map((s) => (
                      <li key={s.shop_id} className="truncate">
                        · {s.shop_name}
                        {s.is_founding_member && <span className="ml-1 text-amber-400">🏆</span>}
                      </li>
                    ))}
                    {shops.length > 8 && (
                      <li className="text-slate-500">… +{shops.length - 8} more</li>
                    )}
                  </ul>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Recent log */}
      <div>
        <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-2">
          Recent Sends ({log.length})
        </div>
        <div className="bg-[#151A23] border border-slate-800 rounded-md overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-900 text-slate-400">
                <tr>
                  <th className="text-left px-4 py-3 font-semibold">When</th>
                  <th className="text-left px-4 py-3 font-semibold">Shop</th>
                  <th className="text-left px-4 py-3 font-semibold">Window</th>
                  <th className="text-left px-4 py-3 font-semibold">Expires</th>
                  <th className="text-left px-4 py-3 font-semibold">By</th>
                  <th className="text-right px-4 py-3 font-semibold">Action</th>
                </tr>
              </thead>
              <tbody>
                {log.length === 0 && (
                  <tr><td colSpan={6} className="px-4 py-10 text-center text-slate-500" data-testid="reminders-log-empty">
                    No reminders dispatched yet.
                  </td></tr>
                )}
                {log.map((r) => (
                  <tr key={r.id} className="border-t border-slate-800 hover:bg-slate-800/40" data-testid={`reminders-log-row-${r.id}`}>
                    <td className="px-4 py-3 text-slate-400 text-xs tabular">{String(r.created_at || "").slice(0, 16).replace("T", " ")}</td>
                    <td className="px-4 py-3 font-medium text-white">
                      {r.shop_name}
                      <div className="text-[10px] text-slate-500 tabular">{r.owner_email}</div>
                    </td>
                    <td className="px-4 py-3 text-slate-300 text-xs">{r.offset_label || `offset ${r.day_offset}`}</td>
                    <td className="px-4 py-3 text-slate-400 text-xs tabular">{String(r.expires_at || "").slice(0, 10)}</td>
                    <td className="px-4 py-3 text-slate-500 text-xs">
                      {r.triggered_by === "scheduler" ? (
                        <span className="inline-flex items-center gap-1"><Hourglass className="w-3 h-3" /> cron</span>
                      ) : "admin"}
                    </td>
                    <td className="px-4 py-3 text-right">
                      {r.wa_link ? (
                        <a
                          href={r.wa_link}
                          target="_blank" rel="noreferrer"
                          className="inline-flex items-center gap-1 text-xs font-semibold rounded-md border border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10 px-2.5 py-1.5"
                          data-testid={`reminders-log-wa-${r.id}`}
                        >
                          <MessageCircle className="w-3 h-3" /> Send WhatsApp
                        </a>
                      ) : (
                        <span className="text-xs text-slate-600">— no mobile —</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}
